import { useState, useEffect, useRef } from 'react'
import AdminHeader from '../../components/admin/AdminHeader'
import AdminSidebar from '../../components/admin/AdminSidebar'
import ImageCropModal from '../../components/admin/ImageCropModal'
import { processFile, formatFileSize, checkFileSize } from '../../utils/fileCompression'
import { API_BASE_URL, REQUIRED_BANNER_HEIGHT, REQUIRED_BANNER_WIDTH } from '../../config/api'

interface HeroItem {
  id: number
  type: 'image' | 'video'
  title: string
  redirect_url?: string
  file_path?: string
  file_url?: string // Keep for backward compatibility
  is_active?: number
  created_at?: string
}

const HeroManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [heroItems, setHeroItems] = useState<HeroItem[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingItem, setViewingItem] = useState<HeroItem | null>(null)
  const [editingItem, setEditingItem] = useState<HeroItem | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [showCropModal, setShowCropModal] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [cropFileName, setCropFileName] = useState('banner.jpg')

  const addFileInputRef = useRef<HTMLInputElement>(null)
  const editFileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    type: 'image' as 'image' | 'video',
    title: '',
    redirect_url: '',
    file: null as File | null,
    is_active: 1
  })


  // Helper function to get full file URL from file_path
  const getFileUrl = (item: HeroItem): string | null => {
    if (item.file_path) {
    
      const cleanPath = item.file_path.replace(/\\/g, '/') // Replace backslashes with forward slashes
      return `${API_BASE_URL}${cleanPath}`
    }
    // Fallback to file_url if file_path doesn't exist (backward compatibility)
    return item.file_url || null
  }

  // Fetch hero items
  const fetchHeroItems = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch(`${API_BASE_URL}hero_list.php?t=${Date.now()}`)
      const result = await response.json()
      
      if (result.status && result.data) {
        setHeroItems(result.data)
      } else {
        setError(result.message || 'Failed to fetch hero items')
      }
    } catch (err) {
      setError('Failed to fetch hero items. Please try again.')
      console.error('Error fetching hero items:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHeroItems()
  }, [])

  // Process file after crop or validation
  const processAndSetFile = async (fileToProcess: File) => {
    setUploadProgress(`Processing file (${formatFileSize(fileToProcess.size)})...`)

    const result = await processFile(fileToProcess)

    if (result.error) {
      setError(result.error)
      return
    }

    setFormData((prev) => ({ ...prev, file: result.file }))
    setUploadProgress(`File ready: ${formatFileSize(result.file.size)} (${REQUIRED_BANNER_WIDTH} × ${REQUIRED_BANNER_HEIGHT} px)`)
  }

  // Handle file selection with cropping
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return

    const file = e.target.files[0]
    setError('')
    setUploadProgress('')

    // Check file size before processing
    const sizeCheck = checkFileSize(file)
    if (!sizeCheck.valid) {
      setError(sizeCheck.message || 'File is too large')
      e.target.value = '' // Reset input
      return
    }

    const currentType = editingItem ? editingItem.type : formData.type

    // Dimension check for images
    if (currentType === 'image') {
      const isValidDimension = await validateImageDimensions(file)

      if (!isValidDimension) {
        // Image does not match 1920 × 1080 -> open cropping modal
        const reader = new FileReader()
        reader.onload = () => {
          setCropImageSrc(reader.result as string)
          setCropFileName(file.name)
          setShowCropModal(true)
        }
        reader.readAsDataURL(file)
        e.target.value = ''
        return
      }
    }

    // Direct process if image is already 1920x1080 or if video
    await processAndSetFile(file)
    e.target.value = ''
  }

  const handleCropSave = async (croppedFile: File) => {
    setShowCropModal(false)
    setCropImageSrc(null)

    // Sync cropped file back into the native input field using DataTransfer
    try {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      if (showAddModal && addFileInputRef.current) {
        addFileInputRef.current.files = dataTransfer.files
      } else if (showEditModal && editFileInputRef.current) {
        editFileInputRef.current.files = dataTransfer.files
      }
    } catch (err) {
      console.error('Error syncing file input with DataTransfer:', err)
    }

    await processAndSetFile(croppedFile)
  }

  const handleCropCancel = () => {
    setShowCropModal(false)
    setCropImageSrc(null)
  }

  const validateImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.src = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve(img.width === REQUIRED_BANNER_WIDTH && img.height === REQUIRED_BANNER_HEIGHT)
      }

      img.onerror = () => resolve(false)
    })
  }

  // Handle create
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    
    if (!formData.file) {
      setError('Please select a file')
      return
    }

    // Final size check before upload
    const sizeCheck = checkFileSize(formData.file)
    if (!sizeCheck.valid) {
      setError(sizeCheck.message || 'File is too large')
      return
    }

    setUploading(true)
    setUploadProgress('Uploading...')

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('type', formData.type)
      formDataToSend.append('title', formData.title)
      
      // Include redirect_url for both image and video types
      formDataToSend.append('redirect_url', formData.redirect_url || '')
      
      formDataToSend.append('file', formData.file)

      const response = await fetch(`${API_BASE_URL}hero_create.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Hero item created successfully!')
        setFormData({
          type: 'image',
          title: '',
          redirect_url: '',
          file: null,
          is_active: 1
        })
        setShowAddModal(false)
        setUploadProgress('')
        fetchHeroItems()
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to create hero item')
      }
    } catch (err) {
      setError('Failed to create hero item. Please try again.')
      console.error('Error creating hero item:', err)
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  // Handle update
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem) return

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      const formDataToSend = new FormData()
      formDataToSend.append('id', editingItem.id.toString())
      formDataToSend.append('type', editingItem.type)
      formDataToSend.append('title', formData.title)
      formDataToSend.append('is_active', formData.is_active.toString())
      
      // Send redirect_url for both image and video types
      formDataToSend.append('redirect_url', formData.redirect_url || '')

      // Add file if a new one is selected (check state first, then input ref)
      const fileToUpload = formData.file || (editFileInputRef.current?.files ? editFileInputRef.current.files[0] : null)
      if (fileToUpload) {
        const sizeCheck = checkFileSize(fileToUpload)
        if (!sizeCheck.valid) {
          setError(sizeCheck.message || 'File is too large')
          setUploading(false)
          return
        }
        formDataToSend.append('file', fileToUpload)
      }

      const response = await fetch(`${API_BASE_URL}hero_update.php`, {
        method: 'POST',
        body: formDataToSend
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Hero item updated successfully!')
        setShowEditModal(false)
        setEditingItem(null)
        setFormData({
          type: 'image',
          title: '',
          redirect_url: '',
          file: null,
          is_active: 1
        })
        fetchHeroItems()
        
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to update hero item')
      }
    } catch (err) {
      setError('Failed to update hero item. Please try again.')
      console.error('Error updating hero item:', err)
    } finally {
      setUploading(false)
    }
  }

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this hero item?')) {
      return
    }

    setError('')
    setSuccess('')

    try {
      const response = await fetch(`${API_BASE_URL}hero_delete.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id })
      })

      const result = await response.json()

      if (result.status) {
        setSuccess('Hero item deleted successfully!')
        fetchHeroItems()
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.message || 'Failed to delete hero item')
      }
    } catch (err) {
      setError('Failed to delete hero item. Please try again.')
      console.error('Error deleting hero item:', err)
    }
  }

  // Open edit modal
  const openEditModal = (item: HeroItem) => {
    setEditingItem(item)
    setFormData({
      type: item.type,
      title: item.title,
      redirect_url: item.redirect_url || '',
      file: null,
      is_active: item.is_active !== undefined ? Number(item.is_active) : 1
    })
    setUploadProgress('')
    setError('')
    setShowEditModal(true)
  }

  // Open view modal
  const openViewModal = (item: HeroItem) => {
    setViewingItem(item)
    setShowViewModal(true)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      type: 'image',
      title: '',
      redirect_url: '',
      file: null,
      is_active: 1
    })
    setError('')
    setUploadProgress('')
    setShowCropModal(false)
    setCropImageSrc(null)
  }

  // Filter hero items
  const filteredHeroItems = filter === 'all' 
    ? heroItems 
    : filter === 'active' 
    ? heroItems.filter(item => Number(item.is_active ?? 1) === 1)
    : heroItems.filter(item => Number(item.is_active ?? 1) === 0)

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="flex">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <main className="flex-1 md:ml-64 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-3xl font-bold text-white">Hero Banner & Video Management</h1>
              <button
                onClick={() => {
                  resetForm()
                  setShowAddModal(true)
                }}
                className="bg-primary-light hover:bg-primary-dark text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add New Hero Item</span>
              </button>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg">
                <p className="text-green-400">{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <button
                onClick={() => setFilter('all')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'all'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                All ({heroItems.length})
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'active'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Active ({heroItems.filter(item => Number(item.is_active ?? 1) === 1).length})
              </button>
              <button
                onClick={() => setFilter('inactive')}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  filter === 'inactive'
                    ? 'bg-primary-light text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                Inactive ({heroItems.filter(item => Number(item.is_active ?? 1) === 0).length})
              </button>
            </div>

            {/* Hero Items Grid */}
            {loading ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <p className="text-gray-400 text-lg">Loading...</p>
              </div>
            ) : filteredHeroItems.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-12 border border-gray-700 text-center">
                <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-400 text-lg">No hero items found in this filter.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHeroItems.map((item) => {
                  const fileUrl = getFileUrl(item)
                  return (
                  <div key={item.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                    <div className="relative h-48 bg-gray-700">
                      {item.type === 'image' && fileUrl ? (
                        <img src={fileUrl} alt={item.title} className="w-full h-full object-cover" />
                      ) : item.type === 'video' && fileUrl ? (
                        <video
                          src={fileUrl}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {item.type === 'image' ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            )}
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 flex flex-col gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            Number(item.is_active ?? 1) === 1
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-600 text-gray-300'
                          }`}
                        >
                          {Number(item.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.type === 'image'
                              ? 'bg-blue-500 text-white'
                              : 'bg-purple-500 text-white'
                          }`}
                        >
                          {item.type === 'image' ? 'Image' : 'Video'}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                      {item.redirect_url && (
                        <p className="text-gray-400 text-sm mb-2 truncate">
                          URL: {item.redirect_url}
                        </p>
                      )}
                      <div className="flex space-x-2 mt-4">
                        <button
                          onClick={() => openViewModal(item)}
                          className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openEditModal(item)}
                          className="flex-1 px-4 py-2 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Add New Hero Item</h2>
              <button
                onClick={() => {
                  setShowAddModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-6">
              <div>
                <label htmlFor="type" className="block text-white font-semibold mb-2">
                  Type
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'image' | 'video', redirect_url: '' })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                >
                  <option value="image">Image Banner</option>
                  <option value="video">Video</option>
                </select>
              </div>

              <div>
                <label htmlFor="title" className="block text-white font-semibold mb-2">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  placeholder="Enter title"
                  required
                />
              </div>

              <div>
                <label htmlFor="redirect_url" className="block text-white font-semibold mb-2">
                  Redirect URL (Optional)
                </label>
                <input
                  type="url"
                  id="redirect_url"
                  value={formData.redirect_url}
                  onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  placeholder="https://example.com"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {formData.type === 'image' 
                    ? 'Clicking on the banner will redirect to this URL' 
                    : 'Clicking on the video will redirect to this URL'}
                </p>
              </div>

              <div>
                <label htmlFor="file" className="block text-white font-semibold mb-2">
                  {formData.type === 'image' ? 'Banner Image' : 'Video File'} (Max 50MB)
                </label>
                {formData.type === 'image' && (
                  <div className="mb-3 p-3 bg-blue-500/20 border border-blue-500 rounded-lg">
                    <p className="text-blue-300 text-sm font-semibold mb-1">📐 Required Banner Size:</p>
                    <p className="text-blue-200 text-sm">
                      <span className="font-bold">{REQUIRED_BANNER_WIDTH} × {REQUIRED_BANNER_HEIGHT} pixels</span>
                      <span className="text-blue-300 ml-2">(Width × Height)</span>
                    </p>
                    <p className="text-blue-200 text-xs mt-1">Please ensure your image matches these exact dimensions.</p>
                  </div>
                )}
                <div className="relative">
                  <input
                    ref={addFileInputRef}
                    type="file"
                    id="file"
                    accept={formData.type === 'image' ? 'image/*' : 'video/*'}
                    onChange={handleFileChange}
                    className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  />
                </div>
                {uploadProgress && (
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm text-green-400 font-medium truncate">{uploadProgress}</p>
                    {formData.type === 'image' && formData.file && (
                      <button
                        type="button"
                        onClick={() => {
                          if (formData.file) {
                            const reader = new FileReader()
                            reader.onload = () => {
                              setCropImageSrc(reader.result as string)
                              setCropFileName(formData.file?.name || 'banner.jpg')
                              setShowCropModal(true)
                            }
                            reader.readAsDataURL(formData.file)
                          }
                        }}
                        className="text-xs bg-primary-light hover:bg-primary-dark text-white px-3 py-1.5 rounded-md font-medium ml-2 whitespace-nowrap transition-colors flex items-center gap-1 shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Crop / Adjust</span>
                      </button>
                    )}
                  </div>
                )}
                {error && (
                  <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                    <p className="text-red-400">{error}</p>
                  </div>
                )}
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Uploading...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Hero Item</h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setEditingItem(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate} className="space-y-6">
              <div>
                <label htmlFor="edit-title" className="block text-white font-semibold mb-2">
                  Title
                </label>
                <input
                  type="text"
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  required
                />
              </div>

              <div>
                <label htmlFor="edit-redirect_url" className="block text-white font-semibold mb-2">
                  Redirect URL
                </label>
                <input
                  type="url"
                  id="edit-redirect_url"
                  value={formData.redirect_url}
                  onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                  placeholder="https://example.com"
                />
                <p className="mt-1 text-xs text-gray-400">
                  {editingItem.type === 'image' 
                    ? 'Clicking on the banner will redirect to this URL' 
                    : 'Clicking on the video will redirect to this URL'}
                </p>
              </div>

              <div>
                <label htmlFor="edit-file" className="block text-white font-semibold mb-2">
                  Update {editingItem.type === 'image' ? 'Image' : 'Video'} (Optional, Max 50MB)
                </label>
                {editingItem.type === 'image' && (
                  <div className="mb-3 p-3 bg-blue-500/20 border border-blue-500 rounded-lg">
                    <p className="text-blue-300 text-sm font-semibold mb-1">📐 Required Banner Size:</p>
                    <p className="text-blue-200 text-sm">
                      <span className="font-bold">{REQUIRED_BANNER_WIDTH} × {REQUIRED_BANNER_HEIGHT} pixels</span>
                      <span className="text-blue-300 ml-2">(Width × Height)</span>
                    </p>
                    <p className="text-blue-200 text-xs mt-1">Please ensure your image matches these exact dimensions.</p>
                  </div>
                )}
                <input
                  ref={editFileInputRef}
                  type="file"
                  id="edit-file"
                  accept={editingItem.type === 'image' ? 'image/*' : 'video/*'}
                  onChange={handleFileChange}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                />
                {uploadProgress && (
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-sm text-green-400 font-medium truncate">{uploadProgress}</p>
                    {editingItem.type === 'image' && formData.file && (
                      <button
                        type="button"
                        onClick={() => {
                          if (formData.file) {
                            const reader = new FileReader()
                            reader.onload = () => {
                              setCropImageSrc(reader.result as string)
                              setCropFileName(formData.file?.name || 'banner.jpg')
                              setShowCropModal(true)
                            }
                            reader.readAsDataURL(formData.file)
                          }
                        }}
                        className="text-xs bg-primary-light hover:bg-primary-dark text-white px-3 py-1.5 rounded-md font-medium ml-2 whitespace-nowrap transition-colors flex items-center gap-1 shadow-sm"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Crop / Adjust</span>
                      </button>
                    )}
                  </div>
                )}
                {!formData.file && getFileUrl(editingItem) && (
                  <p className="mt-2 text-sm text-gray-400">
                    Current {editingItem.type} will be kept if no new file is selected
                  </p>
                  
                )}
                {error && (
                  <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg animate-fadeIn">
                    <p className="text-red-400">{error}</p>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="edit-is_active" className="block text-white font-semibold mb-2">
                  Status
                </label>
                <select
                  id="edit-is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border-2 border-gray-600 focus:outline-none focus:border-primary-light"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingItem(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                    uploading
                      ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                      : 'bg-primary-light hover:bg-primary-dark text-white'
                  }`}
                >
                  {uploading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View/Play Modal */}
      {showViewModal && viewingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4">
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-4xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{viewingItem.title}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setViewingItem(null)
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                {viewingItem.type === 'image' && getFileUrl(viewingItem) ? (
                  <img 
                    src={getFileUrl(viewingItem) || ''} 
                    alt={viewingItem.title} 
                    className="w-full h-auto max-h-[60vh] object-contain mx-auto" 
                  />
                ) : viewingItem.type === 'video' && getFileUrl(viewingItem) ? (
                  <video
                    src={getFileUrl(viewingItem) || ''}
                    controls
                    className="w-full h-auto max-h-[60vh]"
                    autoPlay
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="w-full h-64 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {viewingItem.type === 'image' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      )}
                    </svg>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Type</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    viewingItem.type === 'image'
                      ? 'bg-blue-500 text-white'
                      : 'bg-purple-500 text-white'
                  }`}>
                    {viewingItem.type === 'image' ? 'Image' : 'Video'}
                  </span>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    (viewingItem.is_active ?? 1) === 1
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}>
                    {(viewingItem.is_active ?? 1) === 1 ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {viewingItem.redirect_url && (
                  <div className="md:col-span-2">
                    <label className="block text-gray-400 text-sm mb-1">Redirect URL</label>
                    <a
                      href={viewingItem.redirect_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-light hover:text-primary-dark underline break-all"
                    >
                      {viewingItem.redirect_url}
                    </a>
                  </div>
                )}
              </div>

              <div className="flex space-x-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    openEditModal(viewingItem)
                  }}
                  className="flex-1 px-6 py-3 bg-primary-light hover:bg-primary-dark text-white rounded-lg font-semibold transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setViewingItem(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Image Crop Modal */}
      {showCropModal && cropImageSrc && (
        <ImageCropModal
          imageSrc={cropImageSrc}
          fileName={cropFileName}
          onCropSave={handleCropSave}
          onCancel={handleCropCancel}
          targetWidth={REQUIRED_BANNER_WIDTH}
          targetHeight={REQUIRED_BANNER_HEIGHT}
        />
      )}
    </div>
  )
}

export default HeroManagement

